import { z } from 'zod';
import { boardParams, endpoint, id, memberRole } from './common';

// Joining a board takes two steps: an owner invites someone, and that person accepts. Until they accept, an invitation
// gives no access to anything. An invitation not answered within LIMITS.invitationValidDays is gone.

// An invitation as the person invited sees it.
export const invitation = z.object({
    id: z.string(),
    boardId: z.string(),
    boardTitle: z.string(),
    invitedBy: z.string(),            // who sent it, by name (else email)
    role: memberRole,
    // Accepting a kiosk invitation restricts the account to this board's kiosk display, here and everywhere else.
    kiosk: z.boolean(),
    createdAt: z.string(),
});
export type Invitation = z.infer<typeof invitation>;

// A pending invitation as the board's owner sees it.
export const boardInvitation = z.object({
    id: z.string(),
    email: z.string(),
    role: memberRole,
    kiosk: z.boolean(),
    createdAt: z.string(),
});
export type BoardInvitation = z.infer<typeof boardInvitation>;

// Refused (409) when the person already owns or is on the board, or already has a pending invitation to it.
export const inviteMember = endpoint({
    method: 'POST',
    path: '/inviteMember',
    body: z.object({
        boardId: id,
        emailAddress: z.string().trim().email('emailAddress must be an email address'),
        role: memberRole,
        kiosk: z.boolean().default(false),
    }),
    response: z.object({ invitationId: id }),
});

export const getBoardInvitations = endpoint({
    method: 'GET',
    path: '/boards/:boardId/invitations',
    params: boardParams,
    response: z.object({ invitations: z.array(boardInvitation) }),
});

// Withdraws a pending invitation.
export const cancelInvitation = endpoint({
    method: 'POST',
    path: '/cancelInvitation',
    body: z.object({ invitationId: id }),
    response: z.object({ invitationId: id }),
});

// The person invited accepts (joining the board) or declines. Only a pending invitation addressed to them can be answered.
export const respondToInvitation = endpoint({
    method: 'POST',
    path: '/invitations/respond',
    body: z.object({ invitationId: id, accept: z.boolean() }),
    response: z.object({ boardId: id, accepted: z.boolean() }),
});
